
import { v4 as uuidv4 } from 'uuid';
import { Movie, PendingAction } from "@/types/movie";
import { UNDO_DURATION } from "../movieUtils";
import { debugLog } from "@/utils/debugLogger";
import { SingleListService } from "@/services/movie/singleListService";
import { SINGLE_LIST_TYPE } from "@/hooks/search/types/listConstants";
import { fetchMovieDetails } from "@/services/movieService";

/**
 * Creates a new pending action for a movie property change
 * UPDATED: Auto-commit now fetches complete metadata and passes full movie to onComplete
 */
export const createPendingAction = (
  movie: Movie,
  type: 'favorite' | 'planToWatch',
  fromSection: string,
  onComplete: (tmdbId: number, type: 'favorite' | 'planToWatch', newValue: boolean, movie: Movie) => void,
  removePendingAction: (actionId: string) => void
): PendingAction => {
  // Store the current state so we can undo if needed
  const currentMovieData = { ...movie };
  const newValue = type === 'favorite' ? !movie.favorite : !movie.planToWatch;

  if (!movie.tmdbId) {
    throw new Error("Cannot create pending action for movie without tmdbId");
  }

  const id = uuidv4();
  
  // Log once at the beginning of creating a pending action
  debugLog('action', `Creating pending action: ${type} change for "${movie.title}" from ${fromSection}`);
  
  // Initialize cancelled flag to false
  let cancelled = false;
  
  // Set timeout for auto-committing the change after UNDO_DURATION
  const timeoutId = window.setTimeout(async () => {
    try {
      // Check if action has been cancelled before proceeding
      if (cancelled) {
        debugLog('action', `Auto-commit SKIPPED for "${movie.title}" - action was cancelled`);
        return;
      }
      
      console.log(`Auto-committing ${type} change for "${movie.title}" after undo timeout.`);
      
      // UPDATED: Fetch complete metadata if needed before committing
      let enrichedMovie = { ...movie };
      
      // Check if we need to fetch complete metadata
      const needsMetadata = !movie.runtime || 
                           !movie.releaseDate || 
                           !movie.overview;

      if (needsMetadata && newValue) {
        try {
          console.log(`🔍 [PENDING-ACTION] Fetching complete metadata for ${movie.title}`);
          const fetchedMovie = await fetchMovieDetails(movie.tmdbId);
          
          // Merge fetched metadata with current user flags
          enrichedMovie = {
            ...fetchedMovie,
            favorite: type === 'favorite' ? newValue : movie.favorite,
            planToWatch: type === 'planToWatch' ? newValue : movie.planToWatch
          };
          
          console.log(`🔍 [PENDING-ACTION] Enriched movie data:`, {
            title: enrichedMovie.title,
            hasRuntime: !!enrichedMovie.runtime,
            hasReleaseDate: !!enrichedMovie.releaseDate,
            hasOverview: !!enrichedMovie.overview
          });
        } catch (error) {
          console.warn(`⚠️ [PENDING-ACTION] Failed to fetch metadata, using existing data:`, error);
          // Continue with existing movie data
          if (type === 'favorite') {
            enrichedMovie.favorite = newValue;
          } else {
            enrichedMovie.planToWatch = newValue;
          }
        }
      } else {
        // Update the existing movie with new value
        if (type === 'favorite') {
          enrichedMovie.favorite = newValue;
        } else {
          enrichedMovie.planToWatch = newValue;
        }
      }
      
      // Use SingleListService with enriched movie data
      const listType = type === 'favorite' ? SINGLE_LIST_TYPE.FAVORITE : SINGLE_LIST_TYPE.PLAN_TO_WATCH;
      const result = newValue 
        ? await SingleListService.addToList(enrichedMovie, listType)
        : await SingleListService.removeFromList(movie.tmdbId, listType);
      
      if (!result.success) {
        console.error('← Single-list operation error (auto-commit):', result.error);
        return;
      }
      
      console.log('← Single-list operation success: status updated (auto-commit)');
      
      // UPDATED: Pass the enriched movie to onComplete
      onComplete(movie.tmdbId, type, newValue, enrichedMovie);
      
      // Remove the pending action from UI (slight delay to ensure state update completes)
      setTimeout(() => {
        removePendingAction(id);
      }, 50);
      
    } catch (error) {
      console.error(`Error auto-committing ${type} change:`, error);
    }
  }, UNDO_DURATION);

  // Create the pending action object using TMDB ID for consistency
  return {
    id,
    movieId: movie.tmdbId, // Use TMDB ID for local state tracking
    type,
    timeoutId,
    movieData: currentMovieData,
    timestamp: Date.now(),
    fromSection,
    cancelled: false,
    undo: () => {
      // Mark as cancelled to prevent auto-commit
      cancelled = true;
      debugLog('action', `Undo triggered for ${type} change to "${movie.title}".`);
    },
    optimisticUpdate: () => {
      // Update the local state optimistically to reflect the change immediately
      const optimisticProps = type === 'favorite' 
        ? { favorite: newValue } 
        : { planToWatch: newValue };
      
      return optimisticProps;
    }
  };
};

/**
 * Check if a pending action has expired (older than the undo window)
 */
export const hasPendingExpired = (action: PendingAction): boolean => {
  const age = Date.now() - action.timestamp;
  return age >= UNDO_DURATION;
};
